import find from 'lodash/find';
import { push } from 'connected-react-router';
import { analyticsUtils } from '@zg-rentals/analytics';

// API
import api from '../../utils/api';

// Actions
import { setActiveId } from './UIActions';
import { createMarketingLead, createMarketUpdate } from '../../flux/actions/MarketingActions';

// Models
import AutocompleteAddress from '../../models/AutocompleteAddress';
import Address from '../../models/Address';
import Comparables from '../../models/Comparables';

// Queries
import { getCompsOnlyQuery, getFullQuery } from '../../utils/queries';

// Misc / utils
import { trackEvent, trackGtmEvent } from '../../utils/analytics/trackEvent';
import { gaEvents } from '../../constants/AnalyticsConstants';
import pathsManifest from '../../utils/pathsManifest';
import { encodeFullAddressForUrl } from '../../utils/urlUtils';
import type { QueryDataObjectType } from '../../types/QueryDataObjectType';
import { augmentError } from '../../utils/errors';
import constants from '../../flux/constants';
import errorConstants from '../../constants/ErrorConstants';
import PriceMyRentalCSEvents from '../../constants/PriceMyRentalCSEvents';

// Types
import type {
  AutocompleteAddressApiType,
  ByAddressApiType,
  ComparablesApiType,
  getListingForAddressApiType,
  ListingApiErrorType,
} from '../../types/ApiResponseTypes';

type AugmentedAutocompleteAddressType = AutocompleteAddress & {
  isRecent: boolean;
  unit: string;
};

// ActionCreators... <verb><Noun>
export const goToAddressItem = (addressDataObject: AugmentedAutocompleteAddressType) => {
  // @ts-ignore
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const userEmail = state.user.email;
      const user = state.user;

      dispatch({
        type: constants.ADD_RECENT_SEARCH,
        payload: addressDataObject,
      });

      // Call marketing lead API
      dispatch(
        createMarketingLead({
          fullAddress: addressDataObject.name || '',
          unit: addressDataObject.unit,
          email: userEmail,
        }),
      );

      // Fetch address and market summary and comparables
      const { userAddress } = await dispatch(getAddressAndComparables(addressDataObject));

      // Call marketing update API
      dispatch(
        createMarketUpdate({
          email: userEmail,
          zpid: userAddress.zpid,
          price: userAddress.rentZestimate,
          lat: userAddress.lat,
          lon: userAddress.lon,
        }),
      );

      const encodedFullAddress = encodeFullAddressForUrl({
        address: addressDataObject.name,
        unit: addressDataObject.unit,
      });

      trackEvent(gaEvents.WITH_COMPS_WITH_ZESTIMATE);
      analyticsUtils.trackUAEvent(PriceMyRentalCSEvents.Success({ user }));
      trackGtmEvent(gaEvents.GTM_CUSTOM_RENT_ZESTIMATE);

      return dispatch(push(`${pathsManifest.PriceMyRentalPageResults}/${encodedFullAddress}`));
    } catch (err) {
      // @ts-ignore
      const shouldLogAction = !err.data || !err.data.action;
      augmentError(err, {
        ...(shouldLogAction && {
          action: 'Error in AddressActions#goToAddressItem',
        }),
      });

      throw err;
    }
  };
};

export const getComparables = (params: QueryDataObjectType) => {
  // @ts-ignore
  return async (dispatch) => {
    // Call partial query from API that gets comparables
    const requestBody = getCompsOnlyQuery(params);
    const { data } = await dispatch(fetchGraphQlData(requestBody));
    const userComps = dispatch(setComparables(data));

    return { userComps };
  };
};

export const getAddressAndComparables = (params: AugmentedAutocompleteAddressType) => {
  // @ts-ignore
  return async (dispatch) => {
    // Call full query from API
    const requestBody = getFullQuery(params);
    const { data, errors } = await dispatch(fetchGraphQlData(requestBody));
    const userAddress = await dispatch(setAddress(data));
    const hasZestimate = !find(errors, (error) => error.code === errorConstants.RENT_ZESTIMATE_NOT_FOUND);
    const hasCompsPrices = !find(errors, (error) => error.code === errorConstants.GET_COMPS_PRICE_EMPTY);

    if (!hasCompsPrices && !hasZestimate) {
      throwNonCriticalError({
        message: "We don't have enough data for this address",
        code: errorConstants.RENT_ZESTIMATE_NOT_FOUND,
      });
    }

    if (hasCompsPrices) {
      await dispatch(setComparables(data));
    }

    return {
      userAddress,
    };
  };
};

// API fetches
export const fetchAutocompleteData = (partialAddress: string) => {
  // @ts-ignore
  return async (dispatch) => {
    try {
      const data = await dispatch(api.address.autocomplete(partialAddress));
      const addressSuggestions = data.addresses.map((address: AutocompleteAddressApiType) => {
        return new AutocompleteAddress(address);
      });

      return addressSuggestions;
    } catch (error) {
      augmentError(error, {
        action: 'Error in AddressActions#fetchAutocompleteData',
      });

      throw error;
    }
  };
};

export const fetchGraphQlData = (requestBody: string) => {
  // @ts-ignore
  return async (dispatch, getState) => {
    try {
      const user = getState().user;
      const { data, errors } = await dispatch(api.address.graphQlListing(requestBody));

      /*
                Throw on major errors, otherwise handle errors in places where they cause issues
           */
      if (errors.length > 0) {
        if (containsError(errors, errorConstants.PROPERTY_DETAILS_NOT_FOUND)) {
          // If listing can't be found by our API, throw error and STOP
          trackEvent(gaEvents.CANNOT_FIND_ADDRESS);
          analyticsUtils.trackUAEvent(PriceMyRentalCSEvents.Error({ user }));
          throwNonCriticalError({
            message: 'We were unable to match this address',
            code: errorConstants.PROPERTY_DETAILS_NOT_FOUND,
          });
        }

        if (containsError(errors, errorConstants.GET_COMPS_PRICE_EMPTY)) {
          trackEvent(gaEvents.CANNOT_FIND_COMPS);
          analyticsUtils.trackUAEvent(PriceMyRentalCSEvents.Error({ user }));
        }

        if (containsError(errors, errorConstants.RENT_ZESTIMATE_NOT_FOUND)) {
          trackEvent(gaEvents.CANNOT_FIND_ADDRESS_ZESTIMATE);
          analyticsUtils.trackUAEvent(PriceMyRentalCSEvents.Error({ user }));
        }
      }

      return { data, errors };
    } catch (error) {
      augmentError(error, {
        action: 'Error in AddressActions#fetchGraphQlData',
      });

      throw error;
    }
  };
};

// Setters
export const setAddress = (data: ByAddressApiType) => {
  // @ts-ignore
  return async (dispatch, getState) => {
    const addressData = {
      ...data.byAddress.floorplans[0],
      geo: data.byAddress.geo,
      marketSummary: data.byAddress?.marketSummary,
    };

    // Check if the user logged in - if so, check if they already have a listing for this address
    const isUserLoggedIn: boolean = getState().user.loggedIn;
    if (isUserLoggedIn) {
      const { street, city, state, zip, unit } = addressData.address;
      const fullAddress = street + ',' + city + ', ' + state + ' ' + zip;

      await dispatch(api.address.getListingForAddress(fullAddress, unit))
        .then((res: getListingForAddressApiType) => {
          addressData.addressHasListing = res.listingFound;
          // If listingFound false, then there is no listing (a.k.a undefined)
          if (res.listingFound) {
            addressData.zrmListingId = res.listing.listingId;
          }
          return;
        })
        .catch((error: { message: string }) => {
          augmentError(error, {
            action: 'Error in AddressActions#setAddress',
          });

          throw error;
        });
    }

    // Set the user's address
    const modeledAddressData = new Address(addressData);

    dispatch({
      type: constants.SET_ADDRESS,
      payload: modeledAddressData,
    });

    modeledAddressData.listingId && dispatch(setActiveId(modeledAddressData.listingId));

    return modeledAddressData;
  };
};

export const setComparables = (data: ByAddressApiType) => {
  // @ts-ignore
  return (dispatch, getState) => {
    const {
      floorplans,
      listingRanking,
      lowestPriceIncluded: min,
      highestPriceIncluded: max,
    } = data.byAddress.similarFloorplans;
    const user = getState().user;

    if (floorplans.length === 0) {
      trackEvent(gaEvents.CANNOT_FIND_COMPS, { clickstreamEvent: PriceMyRentalCSEvents.Error({ user }) });
    }

    // Set the user's comparables
    const compsItems = floorplans.map((comp: ComparablesApiType) => {
      return new Comparables(comp);
    });

    const userProperty = getState().address;
    compsItems.splice(listingRanking, 0, userProperty);
    const userPropertyIdx = compsItems.findIndex((comp) => comp.isUsersComp);

    dispatch({
      type: constants.SET_COMPARABLES,
      payload: {
        min,
        max,
        items: compsItems,
        userPropertyIdx,
      },
    });

    // set activeId to userProperty ID after comps load to scroll to home
    dispatch(setActiveId(userProperty.listingId));

    return compsItems;
  };
};

// Errors
const throwNonCriticalError = ({ message, code }: { message: string; code: string }) => {
  throw {
    nonCriticalError: true,
    errorCode: code,
    errorMsg: message,
  };
};

const containsError = (errors: Array<ListingApiErrorType>, errorConstant: string) => {
  return find(errors, (error) => error.code === errorConstant);
};
